import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { billingApi } from './api';
import { queryKeys } from '@/lib/api/queryKeys';
import { useMediaQuery } from '@/lib/hooks/useBreakpoint';
import type { EcPlan } from '@/lib/api/types';
import { PRICING_COPY_EN, PRICING_COPY_ES, type PricingCopy } from './pricing/pricingCopy';
import {
  INITIAL_STATE,
  NEED_IDS,
  PRESETS,
  clamp,
  deepFill,
  handoffUrl,
  pricingConfigOf,
  quote,
  tiersWithCatalog,
  vars,
  type CalcState,
  type DetailKey,
  type MainInputKey,
  type NeedId,
  type PathId,
  type PresetId,
  type PricingLang,
} from './pricing/pricingModel';
import { PlansBlock } from './pricing/PlansBlock';
import { C, SANS, type Mode } from './pricing/ui';

/*
 * `/embed/pricing`: el bloque de planes y calculadora, solo, para que
 * xotski.com lo enmarque (`PlansFrame.tsx` en onxite/ox-xotski-web). Sin
 * cabecera, sin hero, sin pie, con el fondo transparente, y hablando el
 * protocolo que el sitio espera:
 *
 *   xotski:config     una vez al cargar, con la configuración de precios
 *   xotski:height     en cada cambio de tamaño; el marco nunca hace scroll
 *   xotski:navigate   en cada botón; el sitio mueve la ventana entera
 *   xotski:scrollTop  al abrir la calculadora, para no dejar al visitante abajo
 *   ?lang=es          idioma    ·    ?mode=calc    abrir en «Size my year»
 *
 * Los botones no abren aquí ni el checkout ni el alta: dentro de un marco ni
 * Stripe ni el inicio de sesión funcionan. Entregan al visitante a la app en
 * la ventana entera (`register?plan=…` con la configuración) o al formulario
 * de contacto del sitio. Abierta fuera de un marco, la ruta navega ella misma.
 *
 * El idioma no toca las preferencias del usuario de la app: es local a la
 * ruta, porque el marco comparte origen (y almacenamiento) con app.xotski.com.
 */

const HOSTS = new Set(['https://xotski.com', 'https://www.xotski.com', 'https://app.xotski.com']);

function framed(): boolean {
  try { return window.parent !== window; } catch { return true; }
}

/** El origen del anfitrión, si lo conocemos y es de los nuestros; si no, cualquiera. */
function hostOrigin(): string {
  try {
    const ref = document.referrer ? new URL(document.referrer).origin : '';
    if (HOSTS.has(ref) || /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(ref)) return ref;
  } catch { /* sin referrer legible */ }
  return '*';
}

export function PricingEmbedPage() {
  const [params] = useSearchParams();
  const lang: PricingLang = params.get('lang') === 'es' ? 'es' : 'en';
  const rawCopy: PricingCopy = lang === 'es' ? PRICING_COPY_ES : PRICING_COPY_EN;
  const narrow = useMediaQuery('(max-width: 899px)');
  const rootRef = useRef<HTMLDivElement>(null);
  const target = useMemo(hostOrigin, []);
  const post = useCallback((msg: Record<string, unknown>) => {
    if (!framed()) return;
    try { window.parent.postMessage(msg, target); } catch { /* el anfitrión no escucha */ }
  }, [target]);

  const plansQ = useQuery({ queryKey: queryKeys.plans.all, queryFn: billingApi.listPlans });
  const plans: EcPlan[] = useMemo(() => plansQ.data?.items ?? [], [plansQ.data]);
  const T = useMemo(() => tiersWithCatalog(plans), [plans]);
  const c = useMemo(() => deepFill(rawCopy, vars(T, lang)), [rawCopy, T, lang]);

  const [calc, setCalc] = useState<CalcState>(INITIAL_STATE);
  const [preset, setPreset] = useState<PresetId | null>(null);
  const [mode, setMode] = useState<Mode>(params.get('mode') === 'calc' ? 'calc' : 'plans');
  const q = useMemo(() => quote(calc, T), [calc, T]);

  const num = (k: MainInputKey | DetailKey, v: string | number, min: number, max: number) => {
    const n = typeof v === 'number' ? v : parseInt(v, 10);
    if (Number.isNaN(n)) return;
    setCalc((s) => ({ ...s, [k]: clamp(n, min, max) }));
    setPreset(null);
  };
  const step = (k: MainInputKey, d: number, min: number, max: number) => {
    setCalc((s) => ({ ...s, [k]: clamp(s[k] + d, min, max) }));
    setPreset(null);
  };
  const toggleNeed = (k: NeedId) => {
    setCalc((s) => ({ ...s, needs: { ...s.needs, [k]: !s.needs[k] } }));
    setPreset(null);
  };
  const applyPreset = (id: PresetId) => {
    const ps = PRESETS.find((p) => p.id === id)!;
    setCalc({ ...ps.s, needs: Object.fromEntries(NEED_IDS.map((n) => [n, !!ps.needs[n]])) as CalcState['needs'] });
    setPreset(id);
  };
  const onMode = (m: Mode) => {
    setMode(m);
    if (m === 'calc') post({ type: 'xotski:scrollTop' });
  };
  const registerUrl = `${window.location.origin}/register`;
  const go = (tier: PathId) => {
    const url = handoffUrl(tier, calc, lang, registerUrl);
    if (framed()) { post({ type: 'xotski:navigate', url }); return; }
    window.location.assign(url);
  };

  // Fondo transparente y título propios mientras la ruta está montada.
  useEffect(() => {
    const prev = { title: document.title, html: document.documentElement.style.background, body: document.body.style.background };
    document.title = lang === 'es' ? 'Planes y calculadora de Xotski' : 'Xotski plans and calculator';
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    return () => {
      document.title = prev.title;
      document.documentElement.style.background = prev.html;
      document.body.style.background = prev.body;
    };
  }, [lang]);

  // La configuración, cuando el catálogo ya respondió (o falló): así el sitio
  // recibe los números reales y no los del build de relleno.
  const sentConfigFor = useRef<string>('');
  useEffect(() => {
    if (!plansQ.isFetched) return;
    const config = pricingConfigOf(T, registerUrl);
    const key = JSON.stringify(config);
    if (sentConfigFor.current === key) return;
    sentConfigFor.current = key;
    post({ type: 'xotski:config', config });
  }, [T, post, registerUrl, plansQ.isFetched]);

  // La altura, en cada cambio de tamaño del contenido.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const send = () => post({ type: 'xotski:height', height: Math.ceil(el.getBoundingClientRect().height) });
    send();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(send) : null;
    ro?.observe(el);
    window.addEventListener('resize', send);
    const timers = [120, 500, 1200].map((ms) => window.setTimeout(send, ms));
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', send);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [post, mode, plansQ.data]);

  return (
    <div
      ref={rootRef}
      className="xkp"
      data-testid="pricing-embed"
      style={{ background: 'transparent', color: C.text, fontFamily: SANS, fontSize: 16, lineHeight: '24px', overflow: 'hidden' }}
    >
      <PlansBlock
        c={c}
        T={T}
        q={q}
        calc={calc}
        preset={preset}
        mode={mode}
        narrow={narrow}
        onMode={onMode}
        onGo={go}
        onNum={num}
        onStep={step}
        onToggleNeed={toggleNeed}
        onPreset={applyPreset}
        embedded
      />
    </div>
  );
}
